import {
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  format,
  isValid,
  parse,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';

import { WORKFLOW_CHART_DATE_BUCKETS, WORKFLOW_CHART_EMPTY_BUCKET_KEYS } from './workflow-insights';
import type { WorkflowChartDateBucket } from './workflow-insights';

export type ChartDateBucketRange =
  | { kind: 'empty' }
  | { end: string; kind: 'range'; start: string };

/** `2024-01-03` */
export type ChartDateBucketDayKey = `${number}-${number}-${number}`;
/** `2024-W01` */
export type ChartDateBucketWeekKey = `${number}-W${string}`;
/** `2024-02` */
export type ChartDateBucketMonthKey = `${number}-${number}`;

export type ChartDateBucketKey =
  | ChartDateBucketDayKey
  | ChartDateBucketMonthKey
  | ChartDateBucketWeekKey
  | typeof WORKFLOW_CHART_EMPTY_BUCKET_KEYS.date;

/**
 * date-fns tokens that encode and parse Chart date-bucket keys. Week uses the
 * ISO week-numbering year (`RRRR`) so a late-December day can belong to week 1
 * of the next year.
 */
export const CHART_DATE_BUCKET_KEY_FORMATS = {
  [WORKFLOW_CHART_DATE_BUCKETS.day]: 'yyyy-MM-dd',
  [WORKFLOW_CHART_DATE_BUCKETS.month]: 'yyyy-MM',
  [WORKFLOW_CHART_DATE_BUCKETS.week]: "RRRR-'W'II",
} as const satisfies Record<WorkflowChartDateBucket, string>;

const DATE_BUCKET_BOUNDS = {
  [WORKFLOW_CHART_DATE_BUCKETS.day]: (date: Date) => ({
    end: endOfDay(date),
    start: startOfDay(date),
  }),
  [WORKFLOW_CHART_DATE_BUCKETS.month]: (date: Date) => ({
    end: endOfMonth(date),
    start: startOfMonth(date),
  }),
  [WORKFLOW_CHART_DATE_BUCKETS.week]: (date: Date) => ({
    end: endOfISOWeek(date),
    start: startOfISOWeek(date),
  }),
} as const satisfies Record<WorkflowChartDateBucket, (date: Date) => { end: Date; start: Date }>;

export type ParsedChartDateBucketKey =
  | { kind: 'empty' }
  | { bucket: WorkflowChartDateBucket; date: Date; kind: 'date' };

export function encodeChartDateBucketKey(date: Date, dateBucket: WorkflowChartDateBucket): string {
  return format(date, CHART_DATE_BUCKET_KEY_FORMATS[dateBucket]);
}

export function parseChartDateBucketKey(key: string): ParsedChartDateBucketKey | null {
  if (key === WORKFLOW_CHART_EMPTY_BUCKET_KEYS.date) return { kind: 'empty' };

  for (const bucket of Object.values(WORKFLOW_CHART_DATE_BUCKETS)) {
    const date = parseDateWithBucketFormat(key, bucket);
    if (date) return { bucket, date, kind: 'date' };
  }
  return null;
}

/**
 * Convert a Chart Dimension date bucket key (day / ISO week / month / empty)
 * into list filter bounds. Bounds are ISO date-time strings compared with
 * startOfDay on the server.
 */
export function getDateRangeFromChartBucketKey(key: string): ChartDateBucketRange | null {
  const parsed = parseChartDateBucketKey(key);
  if (!parsed) return null;
  if (parsed.kind === 'empty') return { kind: 'empty' };

  const { end, start } = DATE_BUCKET_BOUNDS[parsed.bucket](parsed.date);
  return { end: end.toISOString(), kind: 'range', start: start.toISOString() };
}

function parseDateWithBucketFormat(key: string, bucket: WorkflowChartDateBucket): Date | null {
  const formatStr = CHART_DATE_BUCKET_KEY_FORMATS[bucket];
  const date = parse(key, formatStr, new Date());
  if (!isValid(date)) return null;
  if (format(date, formatStr) !== key) return null;
  return date;
}
